import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import { SophieIcon } from 'src/componentes/icons';
import useAuth from 'src/hooks/useAuth';
import ContentManager from 'src/componentes/content-manager';
import FeedbackSophie from 'src/components/IA/ChatGPT/FeedbackSophie';
import IconsHoverSelect from 'src/componentes/IconsHoverSelect';
import { TIPO_INTERACAO_SOPHIE } from 'src/utils/constants';

const containerStyle = {
  border: '1px solid #BEE6EE',
  borderRadius: '0.5rem',
  marginBottom: '1rem',
};

const headerButtonStyle = {
  alignItems: 'center',
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  display: 'flex',
  gap: '0.625rem',
  justifyContent: 'space-between',
  padding: '1rem',
  width: '100%',
};

const titleStyle = {
  color: '#1D2939',
  fontSize: '0.9375rem',
  fontWeight: 600,
};

const subtitleStyle = {
  color: '#667085',
  fontSize: '0.8125rem',
  marginTop: '0.125rem',
};

const BODY_LINE_HEIGHT = 1.6;
const CLAMPED_LINES = 2;

const bodyWrapperStyle = {
  padding: '0 1rem 1rem',
};

const bodyStyle = {
  color: '#344054',
  fontSize: '0.875rem',
  lineHeight: BODY_LINE_HEIGHT,
  margin: 0,
};

const bodyClampStyle = {
  ...bodyStyle,
  maxHeight: `${CLAMPED_LINES * BODY_LINE_HEIGHT}em`,
  overflow: 'hidden',
};

const markdownComponents = {
  h2: ({ children }) => (
    <h2
      style={{
        color: '#1D2939',
        fontSize: '0.9375rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
      }}
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3
      style={{
        color: '#1D2939',
        fontSize: '0.875rem',
        fontWeight: 700,
        margin: '0.75rem 0 0.375rem 0',
      }}
    >
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p
      style={{
        color: '#344054',
        fontSize: '0.875rem',
        lineHeight: BODY_LINE_HEIGHT,
        margin: '0 0 0.5rem 0',
      }}
    >
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul
      style={{
        color: '#344054',
        fontSize: '0.875rem',
        lineHeight: BODY_LINE_HEIGHT,
        margin: '0 0 0.5rem 0',
        paddingLeft: '1.25rem',
      }}
    >
      {children}
    </ul>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: '0.625rem' }}>{children}</li>
  ),
  strong: ({ children }) => (
    <strong style={{ color: '#1D2939', fontWeight: 700 }}>{children}</strong>
  ),
};

const footerStyle = {
  alignItems: 'center',
  borderTop: '1px solid #DCEEF2',
  color: '#98A2B3',
  display: 'flex',
  fontSize: '0.75rem',
  gap: '0.375rem',
  marginTop: '0.75rem',
  padding: '0.625rem 1rem',
};

const feedbackContainerStyle = {
  alignItems: 'center',
  background: '#F2FBFC',
  border: '1px solid #E5E7EB',
  borderRadius: '0.5rem',
  display: 'flex',
  justifyContent: 'space-between',
  margin: '1rem',
  padding: '1rem',
  gap: '0.25rem',
};

const feedbackTextStyle = {
  flex: 1,
};

const feedbackTitleStyle = {
  color: '#1D2939',
  fontSize: '0.9375rem',
  fontWeight: 600,
  margin: 0,
};

const feedbackSubtitleStyle = {
  color: '#667085',
  fontSize: '0.8125rem',
  margin: '0.25rem 0 0 0',
};

const starsWrapperStyle = {
  display: 'flex',
  marginLeft: '1rem',
  minWidth: 'fit-content',
};

function ResumoActioIA({ isNew, requestId, resumo }) {
  const intl = useIntl();
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(!!isNew);
  const [selectedRating, setSelectedRating] = useState(null);

  const collectFeedback = (ratingValue) => {
    setSelectedRating(ratingValue);
    ContentManager.addContent(
      <FeedbackSophie
        requestId={requestId}
        subdomain={window?.location?.hostname.split('.')[0] || null}
        userId={user?.id}
        interactionType={TIPO_INTERACAO_SOPHIE.INSIGHTS_PESQUISA_CLIMA}
        preSelectedRating={ratingValue}
      />
    );
  };

  return (
    <div style={containerStyle}>
      <button
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((current) => !current)}
        style={headerButtonStyle}
        type="button"
      >
        <div className="d-flex align-items-center" style={{ gap: '0.625rem' }}>
          <SophieIcon size={18} />
          <div className="text-left">
            <div style={titleStyle}>
              {intl.formatMessage({ id: 'labelResumoComActioIA' })}
            </div>
            <div style={subtitleStyle}>
              {intl.formatMessage({ id: 'geradoPorActioIA' })}
            </div>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp color="#667085" size={18} />
        ) : (
          <ChevronDown color="#667085" size={18} />
        )}
      </button>
      <div
        style={
          isExpanded
            ? bodyWrapperStyle
            : { ...bodyWrapperStyle, ...bodyClampStyle }
        }
      >
        <ReactMarkdown components={markdownComponents}>{resumo}</ReactMarkdown>
      </div>

      {isExpanded && (
        <div style={feedbackContainerStyle}>
          <div style={feedbackTextStyle}>
            <div style={feedbackTitleStyle}>
              {intl.formatMessage({ id: 'feedbackSophie' })}
            </div>
            <div style={feedbackSubtitleStyle}>
              {intl.formatMessage({ id: 'compartilheSeuFeedback' })}
            </div>
          </div>
          <div style={starsWrapperStyle}>
            <IconsHoverSelect
              icon="star"
              iconSize={26}
              iconColor="#FDCC0D"
              onClick={(value) => collectFeedback(value)}
              value={selectedRating ?? null}
              itens={[{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }, { id: 5 }]}
            />
          </div>
        </div>
      )}

      <div style={footerStyle}>
        <Info size={14} />
        <span>{intl.formatMessage({ id: 'avisoSophie' })}</span>
      </div>
    </div>
  );
}

export default ResumoActioIA;
